<template>
  <div style="height: 100%">
    <div class="table_header">
      <span style="marginLeft: 20px">
        Folders
      </span>
    </div>

    <q-table
      :columns="columnDefs"
      :rows="rowData"
      row-key="rowKey"
      v-model:pagination="pagination"
      :sort-method="customSort"
      virtual-scroll
      wrap-cells
      :rows-per-page-options="[0]"
      class="tableClass"
    >
      <template v-slot:top>
        <div v-for="(breadCrumb, index) in breadCrumbs" :key="index">
          {{ index > 0 ? "&nbsp;>&nbsp;" : "" }}
          <a @click="setCurrentValues(breadCrumb.newValues)" :style="index === breadCrumbs.length - 1 ? 'color:#0067C1;cursor: pointer;' : 'cursor: pointer;'">{{ breadCrumb.name }}</a>
        </div>
      </template>

      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th auto-width>
            Actions
          </q-th>
          <q-th v-for="col in props.cols" :key="col.name" :props="props">
            {{ col.label }}
          </q-th>
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr :props="props">
          <q-td auto-width nowrap>
            <div title="Metadata" @click="displayMetadata(props.row.record)" class="foldersTableButtonPointer" v-if="props.row.type === 'file'">
              <Icon icon="bx:detail" width="22" />
            </div>
            <div title="Zip Structure" @click="displayTreeModal(props.row.record)" class="foldersTableButtonPointer" v-if="props.row.hasZipStructure">
              <Icon icon="mdi:file-tree-outline" width="22" />
            </div>
            <div title="File Preview" @click="downloadFile(props.row.record)" class="foldersTableButtonPointer" v-if="props.row.type === 'file'">
              <Icon icon="material-symbols:file-present-outline" width="22" />
            </div>
            <div title="Share Document" @click="shareFile(props.row)" class="foldersTableButtonPointer" v-if="props.row.hasShareDocument">
              <Icon icon="uil:share" width="22" />
            </div>
            <div title="Edit Document" @click="editMetadata(props.row.record)" class="foldersTableButtonPointer" v-if="props.row.hasUserEditAccessLevel">
              <Icon icon="material-symbols:edit" width="22" />
            </div>
            <div title="Delete Document" @click="deleteFile(props.row.record)" class="foldersTableButtonPointer" v-if="props.row.hasUserDeleteAccessLevel">
              <Icon icon="material-symbols:delete" width="22" />
            </div>
          </q-td>
          <q-td v-for="col in props.cols" :key="col.name" :props="props" @click="setCurrentValues(props.key)" :style="props.row.type === 'folder' ? 'cursor: pointer;' : ''">
            <div
              :title="getFileCount(props.row)"
              @click="openFileVersionAdmin(props.row.record)"
              :class="props.row.hasUserEditAccessLevel ? 'foldersTableButtonPointer' : 'foldersTableButton'"
              v-if="getFileCount(props.row) > 1"
            >
              <Icon icon="grommet-icons:multiple" width="15" />
            </div>
            {{ col.value }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { computed, onBeforeMount, ref } from "vue";
import AuthHelper from "../helper/AuthHelper.js";
import CommonHelper from "../helper/CommonHelper.js";
import NotificationHelper from "../helper/NotificationHelper";

export default {
  props: {
    uploadContainer: Object,
    resultTree: Object,
    filePreview: Object,
    fileDelete: Object,
    fileVersionAdmin: Object
  },
  components: {
    Icon,
  },
  emits: ["editMetadata", "setSideBarMode"],
  setup (props, {emit}) {
    const searchScopeOptionsArray = ref([]);
    const userEditAccessLevel = ref(false);
    const userDeleteAccessLevel = ref(false);
    const userAdminAccessLevel = ref(false);

    const currentScope = ref();
    const currentFolderStructure = ref();

    onBeforeMount(async () => {
      const userData = await AuthHelper.getCurrentUserData(true, true);
      searchScopeOptionsArray.value = userData.scopesAccess;
      userEditAccessLevel.value = userData.accessLevel.includes("Edit");
      userDeleteAccessLevel.value = userData.accessLevel.includes("Delete");
      userAdminAccessLevel.value = userData.accessLevel.includes("Admin");

      if (searchScopeOptionsArray.value.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const folderParam = urlParams.get("folder");
        const searchScopeParam = urlParams.get("searchScope");

        if (folderParam) {
          await CommonHelper.getIndexCounts();
          if (searchScopeParam && searchScopeOptionsArray.value.includes(searchScopeParam)) {
            setCurrentValues(`${searchScopeParam}:::${folderParam}`);
          } else {
            searchScopeOptionsArray.value.forEach((scope) => {
              let folder = getFolder(scope, folderParam);

              if (folder && folder !== "not found") {
                setCurrentValues(`${scope}:::${folderParam}`);
              }
            })
          }
        }
      }
    });

    function setCurrentValues(newValues) {
      if (newValues === "") {
        currentScope.value = "";
        currentFolderStructure.value = "";
      } else if (newValues.includes(":::")) {
        const newValuesArray = newValues.split(":::");

        currentScope.value = newValuesArray[0];
        currentFolderStructure.value = newValuesArray[1];

        if (currentScope.value) {
          CommonHelper.checkDocumentUpdate();
          CommonHelper.submitMayanServiceIndexSearch(currentScope.value, 1000);

          if (currentFolderStructure.value) {
            if (currentFolderStructure.value !== "*") {
              CommonHelper.submitMayanServiceSingularSearch("folder_structure", currentFolderStructure.value, currentScope.value);
            } else {
              CommonHelper.submitMayanServiceSingularSearch("folder_structure", "", currentScope.value);
            }
          }
        }
      }
    }

    const breadCrumbs = computed(() => {
      let returnValue = [];

      returnValue.push({ name: "Home", newValues: "" });

      if (currentScope.value) {
        returnValue.push({ name: currentScope.value, newValues: `${currentScope.value}:::*` });

        if (currentFolderStructure.value !== "*") {
          let newFolderStructure = "";

          const folderStructureArray = currentFolderStructure.value.split("/");

          for (let i = 0; i < folderStructureArray.length; i++) {
            let folder = folderStructureArray[i];

            newFolderStructure = newFolderStructure !== "" ? `${newFolderStructure}/${folder}` : folder;

            returnValue.push({ name: folder, newValues: `${currentScope.value}:::${newFolderStructure}` });
          }
        }
      }

      return returnValue;
    });

    const columnDefs = ref([
      { label: "Name", name: "name", field: "name", align: "left", sortable: true }
    ]);

    const rowDictionary = computed(() => {
      let returnValue = {};

      if (searchScopeOptionsArray.value.length > 0) {
        CommonHelper.tableInput.value.forEach((record) => {
          if (record.folder_structure && checkRecordAccessUserGroup(record.access_user_group_uid)) {
            addRecordToRowDictionary(record, returnValue);
          }
        })

        if (props.uploadContainer && props.uploadContainer.uploadScope && props.uploadContainer.folder) {
          addUploadFolderToRowDictionary(returnValue);
        }
      }

      return returnValue;
    });

    function checkRecordAccessUserGroup(recordAccessUserGroup) {
      let returnValue = false;

      if (userAdminAccessLevel.value || recordAccessUserGroup === "" || recordAccessUserGroup === undefined) {
        returnValue = true;
      } else {
        const recordAccessUserGroupArray = recordAccessUserGroup.split(":::");
        CommonHelper.currentUserGroup.value.forEach((userGroup) => {
          recordAccessUserGroupArray.forEach((recordUserGroup) => {
            if (recordUserGroup === userGroup.uid) {
              returnValue = true;
            }
          });
        });
      }

      return returnValue;
    }

    function addRecordToRowDictionary(record, dictionary) {
      const folderStructureArray = record.folder_structure.split("/");

      if (dictionary[record.scope] === undefined) {
        dictionary[record.scope] = { files: {}, folders: {} };
      }

      let folder = dictionary[record.scope];

      for (let i = 0; i < folderStructureArray.length; i++) {
        let folderName = folderStructureArray[i];

        if (folder.folders[folderName] === undefined) {
          folder.folders[folderName] = { files: {}, folders: {} };
        }

        folder = folder.folders[folderName];
      }

      folder.files[record.label] = record;
    }

    function addUploadFolderToRowDictionary(dictionary) {
      const folderStructureArray = props.uploadContainer.folder.split("/");

      if (dictionary[props.uploadContainer.uploadScope] === undefined) {
        dictionary[props.uploadContainer.uploadScope] = { files: {}, folders: {} };
      }

      let folder = dictionary[props.uploadContainer.uploadScope];

      for (let i = 0; i < folderStructureArray.length; i++) {
        let folderName = folderStructureArray[i];

        if (folder.folders[folderName] === undefined) {
          folder.folders[folderName] = { files: {}, folders: {} };
        }

        folder = folder.folders[folderName];
      }
    }

    function getFolder(scope, folderStructure) {
      let returnValue = null;
      let notFound = false;

      if (scope) {
        if (rowDictionary.value[scope] === undefined ) {
          notFound = true;
        } else {
          returnValue = rowDictionary.value[scope];
        }

        if (!notFound && folderStructure !== "*") {
          const folderStructureArray = folderStructure.split("/");

          for (let i = 0; i < folderStructureArray.length; i++) {
            if (notFound || returnValue.folders[folderStructureArray[i]] === undefined) {
              notFound = true;
            } else {
              returnValue = returnValue.folders[folderStructureArray[i]];
            }
          }
        }
      }

      return notFound ? "not found" : returnValue;
    }

    const rowData = computed(() => {
      let returnValue = [];

      if (searchScopeOptionsArray.value.length > 0) {
        const hasShareDocument = window.origin === process.env.VUE_APP_URL;

        const currentFolder = getFolder(currentScope.value, currentFolderStructure.value);

        if (currentFolder) {
          if (currentFolder !== "not found") {
            Object.keys(currentFolder.folders).forEach(async (folder) => {
              let newFolderStructure = currentFolderStructure.value !== "*" ? `${currentFolderStructure.value}/${folder}` : folder;

              returnValue.push({
                name: folder,
                type: 'folder',
                rowKey: `${currentScope.value}:::${newFolderStructure}`,
                hasShareDocument: hasShareDocument
              });
            });

            Object.keys(currentFolder.files).forEach(async (filename) => {
              let record = currentFolder.files[filename];

              returnValue.push({
                name: filename,
                type: 'file',
                rowKey: filename,
                record: record,
                hasZipStructure: hasZipStructure(record),
                hasShareDocument: hasShareDocument,
                hasUserEditAccessLevel: hasUserEditAccessLevel(record),
                hasUserDeleteAccessLevel: hasUserDeleteAccessLevel(record)
              });
            });
          }
        } else {
          searchScopeOptionsArray.value.forEach((scope) => {
            returnValue.push({
              name: scope,
              type: 'folder',
              rowKey: `${scope}:::*`,
              hasShareDocument: hasShareDocument
            });
          })
        }
      }

      return returnValue;
    });

    function hasZipStructure(record) {
      return record.label.endsWith(".zip") && CommonHelper.storedZipFileData.value[record.id];
    }

    function hasUserEditAccessLevel(record) {
      return userEditAccessLevel.value && !CommonHelper.nonEditList.includes(record.scope);
    }

    function hasUserDeleteAccessLevel(record) {
      return userDeleteAccessLevel.value && !CommonHelper.nonDeleteList.includes(record.scope);
    }

    const filePreviewRowData = computed(() => {
      let returnValue = rowData.value.filter((row) => {
        return row.type === "file";
      });

      if (pagination.value.sortBy === "name") {
        returnValue = customSort(returnValue, "name", pagination.value.descending);
      }

      return returnValue;
    });

    const pagination = ref({ rowsPerPage: 0, sortBy: "name", ascending: true })

    /*
     * Sort Function for q-table
     */
    function customSort(rows, sortBy, descending) {
      const data = [...rows]

      if (sortBy) {
        data.sort((a, b) => {
          const x = descending ? b : a;
          const y = descending ? a : b;

          if (x.type === "folder" && y.type === "file") {
            return -1;
          } else if (x.type === "file" && y.type === "folder") {
            return 1;
          } else {
            return x.name > y.name ? 1 : x.name < y.name ? -1 : 0;
          }
        })
      }

      return data;
    }

    function displayMetadata(record) {
      CommonHelper.displayMetadata(record.id, record.scope);
      emit("setSideBarMode", "singularSearchHidden");
    }

    function displayTreeModal(record) {
      props.resultTree.displayTreeModal(record.id, record.label);
    }

    function downloadFile(record) {
      props.filePreview.downloadFile(record.id, record.label, true);
    }

    function shareFile(row) {
      if (row.record) {
        navigator.clipboard.writeText(`${process.env.VUE_APP_URL}?document_id=${row.record.id}`);
      } else {
        const rowKeyArray = row.rowKey.split(":::");
        navigator.clipboard.writeText(`${process.env.VUE_APP_URL}?folder=${encodeURIComponent(rowKeyArray[1])}&searchScope=${rowKeyArray[0]}`);
      }

      NotificationHelper.createInfoNotification("Link copied to clipboard");
    }

    function editMetadata(record) {
      emit("editMetadata", record);
    }

    function deleteFile(record) {
      props.fileDelete.deleteRow(record.id, record.label, record.scope);
    }

    function getFileCount(row) {
      let returnValue = 0;

      if (row.record && row.record.file_count) {
        returnValue = row.record.file_count;
      }

      return returnValue;
    }

    function openFileVersionAdmin(record) {
      if (hasUserEditAccessLevel(record)) {
        props.fileVersionAdmin.openFileVersionAdmin(record.id, record.label);
      }
    }

    return {
      //variable properties
      currentScope,
      currentFolderStructure,
      breadCrumbs,
      columnDefs,
      rowDictionary,
      rowData,
      filePreviewRowData,
      pagination,

      //functions
      setCurrentValues,
      customSort,
      displayMetadata,
      displayTreeModal,
      downloadFile,
      shareFile,
      editMetadata,
      deleteFile,
      getFileCount,
      openFileVersionAdmin
    };
  }
};
</script>

<style scoped>
.foldersTableButton {
  display: inline;
  margin-top: 5px;
  margin-right: 8px;
}

.foldersTableButtonPointer {
  display: inline;
  margin-top: 5px;
  margin-right: 8px;
  cursor: pointer;
}
</style>